import React from 'react';
import { 
  CheckCircle2, 
  AlertTriangle, 
  Target, 
  Bot, 
  User,
  FileText,
  Calendar,
  Plus,
  Play,
  Pause,
  UserPlus,
  TrendingUp,
  AlertOctagon
} from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow } from 'date-fns';

export default function TaskHistoryTimeline({ 
  taskHistory, 
  allTasks,
  officers, 
  issues, 
  progressReports,
  activity,
  onItemClick
}) {
  const getOfficer = (email) => officers.find(o => o.user_email === email);

  // Merge all events into a unified timeline
  const timeline = [];
  
  // Use allTasks for full task lifecycle events
  const tasksToProcess = allTasks || [];
  
  // Track officer first involvement
  const officerFirstTask = {};
  
  tasksToProcess.forEach(task => {
    // Task created event
    if (task.created_date) {
      timeline.push({
        type: 'task_created',
        date: task.created_date,
        data: task,
        officer: getOfficer(task.user_email),
        id: `task-created-${task.id}`
      });
      
      // Track first involvement per officer
      const officerEmail = task.user_email;
      if (!officerFirstTask[officerEmail] || new Date(task.created_date) < new Date(officerFirstTask[officerEmail])) {
        officerFirstTask[officerEmail] = task.created_date;
      }
    }
    
    // Task started event
    if (task.started_at) {
      timeline.push({
        type: 'task_started',
        date: task.started_at,
        data: task,
        officer: getOfficer(task.user_email),
        id: `task-started-${task.id}`
      });
    }
    
    // Task blocked event
    if (task.blocked_at) {
      timeline.push({
        type: 'task_blocked',
        date: task.blocked_at,
        data: task,
        officer: getOfficer(task.user_email),
        id: `task-blocked-${task.id}`
      });
    }
    
    // Task completed event
    if (task.completed && task.completed_at) {
      timeline.push({
        type: 'task_completed',
        date: task.completed_at,
        data: task,
        officer: getOfficer(task.user_email),
        id: `task-completed-${task.id}`
      });
    }
  });
  
  // Add officer first involvement events
  Object.entries(officerFirstTask).forEach(([email, date]) => {
    const officer = getOfficer(email);
    if (officer) {
      timeline.push({
        type: 'officer_joined',
        date: date,
        data: { officer_email: email },
        officer: officer,
        id: `officer-joined-${email}`
      });
    }
  });

  // Add issues
  issues?.forEach(issue => {
    if (issue.reported_at) {
      timeline.push({
        type: 'issue_reported',
        date: issue.reported_at,
        data: issue,
        officer: getOfficer(issue.reported_by),
        id: `issue-${issue.id}`
      });
    }
  });

  // Add progress reports with KR updates
  progressReports?.forEach(report => {
    if (report.reported_at) {
      timeline.push({
        type: 'progress_report',
        date: report.reported_at,
        data: report,
        officer: getOfficer(report.reported_by),
        id: `report-${report.id}`
      });
      
      // Add KR impact events from progress reports
      if (report.key_results_progress && report.key_results_progress.length > 0) {
        report.key_results_progress.forEach((krProgress, idx) => {
          if (krProgress.new_value) {
            timeline.push({
              type: 'kr_updated',
              date: report.reported_at,
              data: { ...krProgress, report_id: report.id },
              officer: getOfficer(report.reported_by),
              id: `kr-update-${report.id}-${idx}`
            });
          }
        });
      }
    }
  });

  // Sort by date descending
  timeline.sort((a, b) => new Date(b.date) - new Date(a.date));

  // Group by date
  const groupedTimeline = timeline.reduce((acc, item) => {
    const dateKey = format(new Date(item.date), 'yyyy-MM-dd');
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(item);
    return acc;
  }, {});

  const sortedGroups = Object.entries(groupedTimeline).sort((a, b) => b[0].localeCompare(a[0]));

  if (sortedGroups.length === 0) {
    return (
      <div className="text-center py-8 border border-dashed border-slate-800 rounded-lg text-xs text-slate-600">
        No history yet
      </div>
    );
  }

  return (
    <div className="space-y-4 pr-2">
        {sortedGroups.map(([dateKey, items]) => (
          <div key={dateKey}>
            <div className="flex items-center gap-2 mb-2 sticky top-0 bg-[#0B1221] py-1 z-10">
              <Calendar className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[10px] font-bold text-slate-400 uppercase">
                {format(new Date(dateKey), 'EEEE, MMM d')}
              </span>
              <span className="text-[10px] text-slate-600">
                ({formatDistanceToNow(new Date(dateKey), { addSuffix: true })})
              </span>
            </div>

            <div className="space-y-2 pl-4 border-l border-slate-800">
              {items.map((item, idx) => (
                <TimelineItem 
                  key={`${item.type}-${idx}`} 
                  item={item} 
                  onClick={() => onItemClick?.(item)}
                />
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}

function TimelineItem({ item, onClick }) {
  const isBot = item.officer?.is_bot || item.officer?.custom_role === 'AI Officer';
  
  const baseClasses = "relative rounded p-2.5 cursor-pointer transition-all hover:ring-1 hover:ring-indigo-500/50";

  // Task Created
  if (item.type === 'task_created') {
    return (
      <div data-testid="civ-taskhistorytimeline-clickable-div" className={cn(baseClasses, "bg-slate-800/30 border border-slate-700/50")} onClick={onClick}>
        <div className="absolute -left-[21px] top-3 w-2.5 h-2.5 rounded-full bg-slate-500 border-2 border-[#0B1221]" />
        
        <div className="flex items-start gap-2">
          <Plus className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-slate-500 uppercase mb-0.5">Task Added</p>
            <p className="text-xs text-slate-300 leading-snug line-clamp-1">{item.data.task_description}</p>
            <OfficerBadge officer={item.officer} isBot={isBot} />
          </div>
        </div>
      </div>
    );
  }

  // Task Started
  if (item.type === 'task_started') {
    return (
      <div data-testid="civ-taskhistorytimeline-clickable-div-1" className={cn(baseClasses, "bg-amber-950/20 border border-amber-900/30")} onClick={onClick}>
        <div className="absolute -left-[21px] top-3 w-2.5 h-2.5 rounded-full bg-amber-500 border-2 border-[#0B1221]" />
        
        <div className="flex items-start gap-2">
          <Play className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-amber-500 uppercase mb-0.5">Task Started</p>
            <p className="text-xs text-slate-300 leading-snug line-clamp-1">{item.data.task_description}</p>
            <OfficerBadge officer={item.officer} isBot={isBot} />
          </div>
        </div>
      </div>
    );
  }

  // Task Blocked
  if (item.type === 'task_blocked') {
    return (
      <div data-testid="civ-taskhistorytimeline-clickable-div-2" className={cn(baseClasses, "bg-rose-950/20 border border-rose-900/30")} onClick={onClick}>
        <div className="absolute -left-[21px] top-3 w-2.5 h-2.5 rounded-full bg-rose-500 border-2 border-[#0B1221]" />
        
        <div className="flex items-start gap-2">
          <AlertOctagon className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-rose-500 uppercase mb-0.5">Task Blocked</p>
            <p className="text-xs text-slate-300 leading-snug line-clamp-1">{item.data.task_description}</p>
            <OfficerBadge officer={item.officer} isBot={isBot} />
          </div>
        </div>
      </div>
    );
  }

  // Task Completed
  if (item.type === 'task_completed') {
    return (
      <div data-testid="civ-taskhistorytimeline-clickable-div-3" className={cn(baseClasses, "bg-emerald-950/20 border border-emerald-900/30")} onClick={onClick}>
        <div className="absolute -left-[21px] top-3 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-[#0B1221]" />
        
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-emerald-500 uppercase mb-0.5">Task Conquered</p>
            <p className="text-xs text-slate-200 leading-snug line-clamp-1">{item.data.task_description}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <OfficerBadge officer={item.officer} isBot={isBot} inline />
              {item.data.key_result_focus && (
                <Badge variant="outline" className="text-[9px] h-4 border-emerald-500/30 text-emerald-400">
                  <Target className="w-2.5 h-2.5 mr-1" />
                  KR linked
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Officer Joined
  if (item.type === 'officer_joined') {
    return (
      <div data-testid="civ-taskhistorytimeline-clickable-div-4" className={cn(baseClasses, "bg-cyan-950/20 border border-cyan-900/30")} onClick={onClick}>
        <div className="absolute -left-[21px] top-3 w-2.5 h-2.5 rounded-full bg-cyan-500 border-2 border-[#0B1221]" />
        
        <div className="flex items-start gap-2">
          <UserPlus className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-cyan-500 uppercase mb-0.5">Officer Joined Activity</p>
            <div className="flex items-center gap-2">
              {item.officer?.avatar_url ? (
                <img src={item.officer.avatar_url} className="w-5 h-5 rounded-full object-cover border border-cyan-500/50" alt="" />
              ) : isBot ? (
                <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Bot className="w-3 h-3 text-purple-400" />
                </div>
              ) : (
                <div className="w-5 h-5 rounded-full bg-slate-700 flex items-center justify-center">
                  <User className="w-3 h-3 text-slate-400" />
                </div>
              )}
              <span className="text-sm font-medium text-cyan-300">{item.officer?.callsign || 'Unknown'}</span>
              <span className="text-[10px] text-slate-500">{item.officer?.rank_title}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // KR Updated
  if (item.type === 'kr_updated') {
    return (
      <div data-testid="civ-taskhistorytimeline-clickable-div-5" className={cn(baseClasses, "bg-purple-950/20 border border-purple-900/30")} onClick={onClick}>
        <div className="absolute -left-[21px] top-3 w-2.5 h-2.5 rounded-full bg-purple-500 border-2 border-[#0B1221]" />
        
        <div className="flex items-start gap-2">
          <TrendingUp className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-purple-500 uppercase mb-0.5">Key Result Updated</p>
            <p className="text-xs text-slate-300 leading-snug line-clamp-1">{item.data.description}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-[9px] h-4 border-purple-500/30 text-purple-400">
                → {item.data.new_value}
              </Badge>
              <OfficerBadge officer={item.officer} isBot={isBot} inline />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Issue Reported
  if (item.type === 'issue_reported') {
    return (
      <div data-testid="civ-taskhistorytimeline-clickable-div-6" className={cn(baseClasses, "bg-rose-950/20 border border-rose-900/30")} onClick={onClick}>
        <div className="absolute -left-[21px] top-3 w-2.5 h-2.5 rounded-full bg-rose-500 border-2 border-[#0B1221]" />
        
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-rose-500 uppercase mb-0.5">Issue Reported</p>
            <p className="text-xs text-slate-200 leading-snug line-clamp-1">{item.data.title}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <OfficerBadge officer={item.officer} isBot={isBot} inline />
              <Badge variant="outline" className="text-[9px] h-4 border-rose-500/30 text-rose-400 capitalize">
                {item.data.severity || 'medium'}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Progress Report
  if (item.type === 'progress_report') {
    return (
      <div data-testid="civ-taskhistorytimeline-clickable-div-7" className={cn(baseClasses, "bg-indigo-950/20 border border-indigo-900/30")} onClick={onClick}>
        <div className="absolute -left-[21px] top-3 w-2.5 h-2.5 rounded-full bg-indigo-500 border-2 border-[#0B1221]" />
        
        <div className="flex items-start gap-2">
          <FileText className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-indigo-500 uppercase mb-0.5">Progress Report</p>
            <p className="text-xs text-slate-200 leading-snug">Report submitted</p>
            <div className="flex items-center gap-2 mt-1.5">
              <OfficerBadge officer={item.officer} isBot={isBot} inline />
              <Badge 
                variant="outline" 
                className={cn(
                  "text-[9px] h-4",
                  item.data.activity_status === 'green' ? "border-emerald-500/30 text-emerald-400" :
                  item.data.activity_status === 'red' ? "border-rose-500/30 text-rose-400" :
                  "border-amber-500/30 text-amber-400"
                )}
              >
                {item.data.activity_status}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function OfficerBadge({ officer, isBot, inline }) {
  if (inline) {
    return (
      <div className="flex items-center gap-1">
        {officer?.avatar_url ? (
          <img src={officer.avatar_url} className="w-4 h-4 rounded-full object-cover" alt="" />
        ) : isBot ? (
          <Bot className="w-3 h-3 text-purple-400" />
        ) : (
          <User className="w-3 h-3 text-slate-500" />
        )}
        <span className="text-[10px] text-slate-400">{officer?.callsign || 'Unknown'}</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-1 mt-1.5">
      {officer?.avatar_url ? (
        <img src={officer.avatar_url} className="w-4 h-4 rounded-full object-cover" alt="" />
      ) : isBot ? (
        <Bot className="w-3 h-3 text-purple-400" />
      ) : (
        <User className="w-3 h-3 text-slate-500" />
      )}
      <span className="text-[10px] text-slate-400">{officer?.callsign || 'Unknown'}</span>
    </div>
  );
}